import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,         setUser]         = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [accessToken,  setAccessToken]  = useState(() => localStorage.getItem('access_token'));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('refresh_token'));

  /* ── API helper with auto-refresh ─────────────────────────────── */
  const apiFetch = useCallback(async (path, options = {}) => {
    const doFetch = (token) => fetch(`${API}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

    let res = await doFetch(accessToken);

    // Token expired — try refresh
    if (res.status === 401 && refreshToken) {
      const rr = await fetch(`${API}/api/auth/token/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      });
      if (rr.ok) {
        const data = await rr.json();
        setAccessToken(data.access);
        localStorage.setItem('access_token', data.access);
        res = await doFetch(data.access);
      } else {
        // Refresh failed — log out
        logout();
        return null;
      }
    }
    return res;
  }, [accessToken, refreshToken]);

  /* ── Load user + subscription ──────────────────────────────────── */
  const loadUser = useCallback(async () => {
    if (!accessToken) { setLoading(false); return; }
    try {
      const [userRes, subRes] = await Promise.all([
        apiFetch('/api/auth/me/'),
        apiFetch('/api/subscriptions/me/'),
      ]);
      if (userRes?.ok) setUser(await userRes.json());
      if (subRes?.ok)  setSubscription(await subRes.json());
    } catch (e) {
      console.error('Auth load failed', e);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => { loadUser(); }, []);

  /* ── Login ─────────────────────────────────────────────────────── */
  const login = async (email, password) => {
    const res = await fetch(`${API}/api/auth/token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || data.email?.[0] || 'Login failed');

    localStorage.setItem('access_token',  data.access);
    localStorage.setItem('refresh_token', data.refresh);
    setAccessToken(data.access);
    setRefreshToken(data.refresh);

    // Load user data
    const [userRes, subRes] = await Promise.all([
      fetch(`${API}/api/auth/me/`, { headers: { Authorization: `Bearer ${data.access}` } }),
      fetch(`${API}/api/subscriptions/me/`, { headers: { Authorization: `Bearer ${data.access}` } }),
    ]);
    if (userRes.ok) setUser(await userRes.json());
    if (subRes.ok)  setSubscription(await subRes.json());
    return data;
  };

  /* ── Register ──────────────────────────────────────────────────── */
  const register = async (email, password, name) => {
    const res = await fetch(`${API}/api/auth/register/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.email?.[0] || data.detail || 'Registration failed');
    return login(email, password);
  };

  /* ── Logout ────────────────────────────────────────────────────── */
  const logout = async () => {
    if (refreshToken) {
      try {
        await fetch(`${API}/api/auth/token/blacklist/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        });
      } catch {}
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setSubscription(null);
  };

  /* ── Refresh subscription ──────────────────────────────────────── */
  const refreshSubscription = async () => {
    const res = await apiFetch('/api/subscriptions/me/');
    if (res?.ok) setSubscription(await res.json());
  };

  const value = {
    user, subscription, loading,
    isAuthenticated: !!user,
    tier: subscription?.tier || 'free',
     isTrial:           subscription?.is_trial        || false,
    trialEndsAt:       subscription?.trial_ends_at   || null,
    trialDaysRemaining:subscription?.trial_days_remaining || 0,
    removesAds:    subscription?.removes_ads     || false,
    allowsAiTools: subscription?.allows_ai_tools || false,
    allowsApiKey:  subscription?.allows_api_key  || false,
    allowsChromeExtension: subscription?.allows_chrome_extension || false,
    login, register, logout, apiFetch, refreshSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

const FLAG_MAP = {
  ai_tools:      'allowsAiTools',
  ai_tools_plus: 'allowsChromeExtension',
  ai_premium:    'allowsChromeExtension',
};

export function TierGate({ requires, children, fallback = null }) {
  const auth = useAuth();
  const flagKey = FLAG_MAP[requires];
  const hasAccess = flagKey ? auth[flagKey] : false;
  return hasAccess ? children : fallback;
}
